import { Card, CardContent, CardHeader, CardTitle, ErrorState, Skeleton } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { PERMISSIONS } from "@/constants/permissions";
import { useUser } from "@/hooks/queries/user.queries";
import { DarwinUserFilter } from "@/pages/developer/darwin/components/darwin-user-filter";
import { useGetDarwinCost } from "@/queries/darwin.queries";
import { DarwinCostSummary } from "@/types/darwin-cost.types";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";

const CostSearchSchema = z.object({
    userId: z.string().optional(),
});

const countFormat = new Intl.NumberFormat();
const priceFormat = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
});

export const Route = createFileRoute(APP_ROUTES_FULL.DEVELOPER_DARWIN_COST)({
    component: DarwinCostPage,
    validateSearch: searchValidator(CostSearchSchema),
    staticData: {
        config: {
            title: "developer_darwin_cost_title",
            breadcrumb: {
                title: "developer_darwin_cost_title",
                to: APP_ROUTES_TO.DEVELOPER_DARWIN_COST,
            },
        },
        permissions: [PERMISSIONS.CALL_SVC_ADMIN_BASE],
    },
});

function CostStats({ summary, isLoading }: { summary?: DarwinCostSummary; isLoading: boolean }) {
    const { t } = useTranslation();
    const stats = [
        { label: t("developer_darwin_cost_chats"), value: summary ? countFormat.format(summary.conversations) : null },
        {
            label: t("developer_darwin_cost_messages"),
            value: summary ? countFormat.format(summary.messagesSent) : null,
        },
        { label: t("developer_darwin_cost_tokens"), value: summary ? countFormat.format(summary.tokensUsed) : null },
        { label: t("developer_darwin_cost_price"), value: summary ? priceFormat.format(summary.price) : null },
    ];

    return (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat) => (
                <div key={stat.label} className="rounded-xl border border-border/70 px-4 py-3">
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                    {isLoading || stat.value === null ? (
                        <Skeleton className="mt-2 h-8 w-24" />
                    ) : (
                        <p className="mt-1 text-2xl font-semibold tabular-nums">{stat.value}</p>
                    )}
                </div>
            ))}
        </div>
    );
}

function DarwinCostPage() {
    const { t } = useTranslation();
    const navigate = Route.useNavigate();
    const userId = Route.useSearch().userId;
    const { data, isLoading, isError } = useGetDarwinCost(userId);
    const userSummary = data?.user;
    const { data: selectedUser } = useUser(userId);

    const setUserId = (nextUserId?: string) => {
        navigate({ search: { userId: nextUserId }, replace: true });
    };

    return (
        <div className="flex flex-col gap-4">
            <Card>
                <CardHeader>
                    <CardTitle>{t("developer_darwin_cost_overview")}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {isError ? <ErrorState title={t("developer_darwin_cost_loading_error")} /> : null}
                    {!isError ? <CostStats summary={data?.overview} isLoading={isLoading && !data} /> : null}
                    <p className="text-sm text-muted-foreground">{t("developer_darwin_cost_note")}</p>
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <CardTitle>{userId ? (selectedUser?.name ?? userId) : t("developer_darwin_cost_user")}</CardTitle>
                    <DarwinUserFilter userId={userId} onUserIdChange={setUserId} />
                </CardHeader>
                <CardContent className="space-y-4">
                    {userId && !isError ? (
                        <CostStats summary={userSummary} isLoading={isLoading && !userSummary} />
                    ) : null}
                    {!userId ? (
                        <p className="text-sm text-muted-foreground">{t("developer_darwin_cost_select_user")}</p>
                    ) : null}
                </CardContent>
            </Card>
        </div>
    );
}
